import React, { useState, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { useEhrVendors, EhrVendor } from "hooks/use-ehr-vendors";
import { useEhrConnection } from "hooks/use-ehr-connection";
import { useFeatureFlags } from "context/admin-content";
import { Loading } from "components/loading";
import {
  Container,
  Header,
  Title,
  Subtitle,
  SearchSection,
  SearchInput,
  SectionLabel,
  VendorLogo,
  VendorName,
  VendorList,
  VendorListItem,
  ActionButtons,
  PrimaryButton,
  SecondaryButton,
  PrivacyNote,
  LockIcon,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  SearchIcon,
  EmptyStateTitle,
  EmptyStateText,
} from "./styles";

function VendorIcon({ name }: { name: string }) {
  // Simple letter-based icon when no logo is available
  return <span style={{ fontSize: 24, fontWeight: 600 }}>{name.charAt(0)}</span>;
}

// View for users who already have an active EHR connection
function ExistingConnectionView({
  ehrName,
  onRefresh,
  onContinue,
  onSwitchProvider,
  onSkip,
  isRefreshing,
  isSwitching,
}: {
  ehrName: string;
  onRefresh: () => void;
  onContinue: () => void;
  onSwitchProvider: () => void;
  onSkip: () => void;
  isRefreshing: boolean;
  isSwitching: boolean;
}) {
  return (
    <Container>
      <Header>
        <Title>Health Records Connected</Title>
        <Subtitle>
          Your records from {ehrName} are already connected. You can continue
          with your existing records, refresh the data, or switch to a different provider.
        </Subtitle>
      </Header>

      <ActionButtons style={{ marginTop: 32 }}>
        <PrimaryButton onClick={onContinue}>
          Continue with Existing Records
        </PrimaryButton>

        <SecondaryButton onClick={onRefresh} disabled={isRefreshing}>
          {isRefreshing ? "Refreshing..." : "Refresh My Records"}
        </SecondaryButton>

        <SecondaryButton onClick={onSwitchProvider} disabled={isSwitching}>
          {isSwitching ? "Switching..." : "Connect to Different Provider"}
        </SecondaryButton>

        <SecondaryButton
          onClick={onSkip}
          style={{ color: "#666", borderColor: "#ccc", fontSize: 14 }}
        >
          Skip & Enter Manually
        </SecondaryButton>
      </ActionButtons>

      <PrivacyNote>
        <LockIcon>🔒</LockIcon>
        <span>
          Your health information is protected by HIPAA regulations.
        </span>
      </PrivacyNote>
    </Container>
  );
}

export function EhrConnectPage() {
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVendor, setSelectedVendor] = useState<EhrVendor | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [showVendorSelection, setShowVendorSelection] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { enablePatientEhrImport } = useFeatureFlags();
  const { vendors, isLoading: isSearching, search } = useEhrVendors();
  const { initiateConnection, hasActiveConnection, connection, refreshConnection, revokeConnection, isLoading: isLoadingConnection } = useEhrConnection();

  // Debounced search query
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

  // Redirect if EHR import is disabled for this organization
  useEffect(() => {
    if (enablePatientEhrImport === false) {
      // Skip EHR connection and redirect to home
      sessionStorage.setItem("ehr_skipped", "true");
      sessionStorage.setItem("ehr_flow_complete", "true");
      history.replace("/");
    }
  }, [enablePatientEhrImport, history]);

  // Trigger search when debounced query changes
  useEffect(() => {
    if (debouncedSearchQuery.length >= 2) {
      search(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, search]);

  const handleConnect = useCallback(async () => {
    if (!selectedVendor) return;

    setIsConnecting(true);
    setError(null);

    // Store return path for after OAuth
    sessionStorage.setItem("ehr_return_path", "/ehr-importing");

    const redirectUri = `${window.location.origin}/ehr-callback`;

    const result = await initiateConnection(selectedVendor.id, redirectUri);

    if (result) {
      // Store state for verification
      sessionStorage.setItem("ehr_oauth_state", result.state);

      // Redirect to EHR vendor's authorization page
      window.location.href = result.authorizationUrl;
    } else {
      setError("Failed to initiate connection. Please try again.");
      setIsConnecting(false);
    }
  }, [selectedVendor, initiateConnection]);

  const handleSkip = useCallback(() => {
    // Skip EHR connection, mark as skipped and flow complete
    sessionStorage.setItem("ehr_skipped", "true");
    sessionStorage.setItem("ehr_flow_complete", "true");
    history.push("/");
  }, [history]);

  const handleContinueWithExisting = useCallback(() => {
    // Continue with existing connection
    sessionStorage.setItem("ehr_connected", "true");
    sessionStorage.setItem("ehr_flow_complete", "true");
    history.push("/");
  }, [history]);

  const handleRefresh = useCallback(async () => {
    if (!connection?.id) return;
    
    setIsRefreshing(true);
    try {
      await refreshConnection(connection.id);
      // After refresh, go to import status page
      history.push("/ehr-importing");
    } catch (err) {
      setError("Failed to refresh connection. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  }, [connection, refreshConnection, history]);

  const handleSwitchProvider = useCallback(async () => {
    if (!connection?.id) return;
    
    setIsSwitching(true);
    try {
      await revokeConnection(connection.id);
      // Show vendor selection UI instead of navigating away
      setShowVendorSelection(true);
    } catch (err) {
      setError("Failed to disconnect. Please try again.");
    } finally {
      setIsSwitching(false);
    }
  }, [connection, revokeConnection]);

  const showSearchResults = searchQuery.length >= 2;

  if (isLoadingConnection) {
    return <Loading />;
  }

  // Show existing connection view if user has an active connection
  // Unless they chose to switch providers
  if (hasActiveConnection && connection && !showVendorSelection) {
    return (
      <ExistingConnectionView
        ehrName={connection.ehr?.name || connection.vendor?.name || "your healthcare provider"}
        onRefresh={handleRefresh}
        onContinue={handleContinueWithExisting}
        onSwitchProvider={handleSwitchProvider}
        onSkip={handleSkip}
        isRefreshing={isRefreshing}
        isSwitching={isSwitching}
      />
    );
  }

  return (
    <Container>
      <Header>
        <Title>Connect Your Health Records</Title>
        <Subtitle>
          Save time by importing your medical history automatically. Your data is
          secure and encrypted.
        </Subtitle>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <SearchSection>
        <SearchInput
          type="text"
          placeholder="Search for your healthcare provider..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchSection>

      {!showSearchResults && (
        <EmptyState>
          <SearchIcon aria-hidden>🔍</SearchIcon>
          <EmptyStateTitle>Search for your provider</EmptyStateTitle>
          <EmptyStateText>
            Enter your healthcare provider’s name above to find and connect your health records.
          </EmptyStateText>
        </EmptyState>
      )}

      {showSearchResults && (
        <>
          <SectionLabel>Search Results</SectionLabel>
          {isSearching ? (
            <LoadingSpinner>
              <Loading />
            </LoadingSpinner>
          ) : vendors.length > 0 ? (
            <VendorList>
              {vendors.map((vendor) => (
                <VendorListItem
                  key={vendor.id}
                  $selected={selectedVendor?.id === vendor.id}
                  onClick={() => setSelectedVendor(vendor)}
                >
                  <VendorLogo>
                    {vendor.logo_url ? (
                      <img src={vendor.logo_url} alt={vendor.name} />
                    ) : (
                      <VendorIcon name={vendor.name} />
                    )}
                  </VendorLogo>
                  <VendorName style={{ marginLeft: 12 }}>{vendor.name}</VendorName>
                </VendorListItem>
              ))}
            </VendorList>
          ) : (
            <p style={{ color: "#666", textAlign: "center", padding: 20 }}>
              No healthcare providers found. Try a different search term.
            </p>
          )}
        </>
      )}

      <ActionButtons>
        <PrimaryButton
          onClick={handleConnect}
          disabled={!selectedVendor || isConnecting}
        >
          {isConnecting ? "Connecting..." : "Connect My Records"}
        </PrimaryButton>

        <SecondaryButton onClick={handleSkip}>
          Skip, I'll enter manually
        </SecondaryButton>
      </ActionButtons>

      <PrivacyNote>
        <LockIcon>🔒</LockIcon>
        <span>
          Your health information is protected by HIPAA regulations. We only access
          data you authorize.
        </span>
      </PrivacyNote>
    </Container>
  );
}

export default EhrConnectPage;
